import React from "react";
import styles from "./MatrixFond.module.css";
import {resoudre} from "../reglages";
import {REGLAGES} from "./reglages";

/*
 * Fond « Matrix » : une pluie de code vert qui tombe en colonnes.
 * Inspiré de la « pluie de code » du film The Matrix ; dessin et code entièrement originaux, en CSS pur.
 * Chaque colonne est tirée avec sa propre graine : le fond est identique à chaque affichage et sur chaque écran, et
 * changer le nombre de colonnes ne change pas le contenu des autres.
 * Réglages (reglages.js) : nombre de colonnes, vitesse, taille des caractères, couleur.
 */

// katakana demi-chasse (comme dans le film), chiffres et quelques signes
const CARACTERES = "ｱｲｳｴｵｶｷｸｹｺｻｼｽｾｿﾀﾁﾂﾃﾄﾅﾆﾇﾈﾉﾊﾋﾌﾍﾎﾏﾐﾑﾒﾓﾔﾕﾖﾗﾘﾙﾚﾛﾜﾝ0123456789:=*+-<>|";

/** Couleurs de la pluie : [rgb de la lueur] sert aux halos, [bord] à l'assombrissement de la zone de droite. */
const PALETTES = {
    vert:   {pluie: "#00d83a", tete: "#e6ffec", lueur: "0, 255, 65",    fond1: "#02200a", fond2: "#000b03", bord: "0, 8, 2"},
    bleu:   {pluie: "#2f9bff", tete: "#e8f4ff", lueur: "60, 160, 255",  fond1: "#04203f", fond2: "#000812", bord: "0, 6, 16"},
    ambre:  {pluie: "#ffb000", tete: "#fff4d6", lueur: "255, 176, 0",   fond1: "#2a1a00", fond2: "#0c0700", bord: "10, 6, 0"},
    rouge:  {pluie: "#ff3b3b", tete: "#ffe6e6", lueur: "255, 70, 70",   fond1: "#300606", fond2: "#0c0000", bord: "12, 0, 0"},
    violet: {pluie: "#b36bff", tete: "#f3e8ff", lueur: "190, 120, 255", fond1: "#230a40", fond2: "#07000f", bord: "8, 0, 16"},
};

/** Générateur pseudo-aléatoire à graine (mulberry32) : mêmes colonnes à chaque fois. */
function aleatoire(graine) {
    return () => {
        graine = (graine + 0x6D2B79F5) | 0;
        let t = Math.imul(graine ^ (graine >>> 15), 1 | graine);
        t = (t + Math.imul(t ^ (t >>> 7), 61 | t)) ^ t;
        return ((t ^ (t >>> 14)) >>> 0) / 4294967296;
    };
}

/** Les `n` colonnes, à la vitesse et à la taille d'origine (le réglage de vitesse et de taille s'applique à l'affichage). */
function construireColonnes(n) {
    return Array.from({length: n}, (_, i) => {
        const hasard = aleatoire(20261007 + i * 7919);
        const caractere = () => CARACTERES[Math.floor(hasard() * CARACTERES.length)];
        const longueur = 6 + Math.floor(hasard() * 8);
        const profondeur = hasard();            // 0 = loin (petit, terne, lent), 1 = près (grand, vif, rapide)
        const duree = 7 - profondeur * 3.6;     // de 3,4 s à 7 s pour traverser le bandeau
        return {
            gauche: ((i + 0.15 + hasard() * 0.7) / n) * 100,
            taille: 12 + profondeur * 5,
            opacite: (0.45 + profondeur * 0.55).toFixed(2),
            duree,
            decalage: hasard(),                 // part de la durée déjà écoulée à l'ouverture de la page
            texte: Array.from({length: longueur}, caractere).join(""),
            tete: caractere(),
        };
    });
}

/**
 * Fond « Matrix » du bandeau. Remplit son parent.
 *
 * @param reglages  (facultatif) valeurs choisies dans le tableau de bord ; vérifiées ici, valeurs d'origine sinon
 */
export default function MatrixFond({reglages}) {
    const r = resoudre(REGLAGES, reglages);
    const colonnes = React.useMemo(() => construireColonnes(r.colonnes), [r.colonnes]);
    const palette = PALETTES[r.couleur] || PALETTES.vert;

    return (
        <div className={styles.scene} aria-hidden="true"
             style={{"--pluie": palette.pluie, "--tete": palette.tete, "--lueur": palette.lueur, "--fond1": palette.fond1, "--fond2": palette.fond2, "--bord": palette.bord}}>
            {colonnes.map((c, i) => {
                const duree = c.duree / r.vitesse;
                return (
                    <div key={i} className={styles.colonne}
                         style={{left: c.gauche + "%", fontSize: Math.round(c.taille * r.taille), opacity: c.opacite,
                             animationDuration: duree.toFixed(2) + "s", animationDelay: "-" + (c.decalage * duree).toFixed(2) + "s"}}>
                        {c.texte}<b className={styles.tete}>{c.tete}</b>
                    </div>
                );
            })}
        </div>
    );
}
